import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useStepper } from "@/components/ui/stepper";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTeams } from "@/contexts/teams-provider";
import { useLocalStorage } from "@/hooks/useStorage";
import { useTranslation } from "@/hooks/useTranslation";
import { useUser } from "@clerk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SetupTeamCreationInfosDTO } from "@shaarp/common";
import { HelpCircleIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { FiChevronRight, FiChevronsRight } from "react-icons/fi";
import * as z from "zod/v3";

export const SetupTeamCreationInfosPage = () => {
  const { nextStep, prevStep } = useStepper();
  const { teamCompanySelected } = useTeams();
  const { user } = useUser();
  const { t } = useTranslation();
  const [setupTeamCreationInfosDatas, setSetupTeamCreationInfosDatas] =
    useLocalStorage<SetupTeamCreationInfosDTO>("setup-team-creation-infos", {
      jobTitle: null,
      siret: teamCompanySelected?.business?.siret || null,
      website: teamCompanySelected?.website || null,
      linkedin: teamCompanySelected?.socialNetworks?.linkedin.url || null,
      instagram: teamCompanySelected?.socialNetworks?.instagram.url || null,
      referralCode: null,
    });
  const [referralCode] = useLocalStorage<string>("referralCode");

  // Create the form schema with translations
  const FormSchema = z.object({
    jobTitle: z
      .string()
      .min(2, {
        message: t("setup.teamCreation.informations.fields.jobTitle.error"),
      })
      .optional()
      .transform((val) => val || null)
      .nullable(),
    siret: z
      .string()
      .length(14, {
        message: t(
          "setup.teamCreation.informations.fields.sources.siret.error.length"
        ),
      })
      .regex(/^\d+$/, {
        message: t(
          "setup.teamCreation.informations.fields.sources.siret.error.length"
        ),
      })
      .optional()
      .transform((val) => val || null)
      .nullable(),
    website: z
      .string()
      .regex(/^$|^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/, {
        message: t(
          "setup.teamCreation.informations.fields.sources.website.error"
        ),
      })
      .optional()
      .transform((val) => val || null)
      .nullable(),
    linkedin: z
      .string()
      .regex(
        /^$|^https:\/\/(www\.)?(fr\.)?linkedin\.com\/(company|school|in)\/.*$/,
        {
          message: t(
            "setup.teamCreation.informations.fields.sources.linkedin.error"
          ),
        }
      )
      .optional()
      .transform((val) => val || null)
      .nullable(),
    instagram: z
      .string()
      .regex(/^$|^https:\/\/(www\.)?instagram\.com\/.*$/, {
        message: t(
          "setup.teamCreation.informations.fields.sources.instagram.error"
        ),
      })
      .optional()
      .transform((val) => val || null)
      .nullable(),
    referralCode: z
      .string()
      .optional()
      .transform((val) => val || null)
      .nullable(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema as any) as any,
    defaultValues: {
      jobTitle: setupTeamCreationInfosDatas?.jobTitle || null,
      siret: setupTeamCreationInfosDatas?.siret || null,
      website: setupTeamCreationInfosDatas?.website || null,
      linkedin: setupTeamCreationInfosDatas?.linkedin || null,
      instagram: setupTeamCreationInfosDatas?.instagram || null,
      referralCode: referralCode || null,
    },
  });

  useEffect(() => {
    const email = user?.primaryEmailAddress?.emailAddress;
    if (email) {
      const domain = email.split("@")[1];
      if (
        ![
          "gmail.com",
          "hotmail.com",
          "hotmail.fr",
          "outlook.com",
          "yahoo.com",
          "yahoo.fr",
          "orange.fr",
          "laposte.net",
          "laposte.fr",
          "free.fr",
          "sfr.fr",
        ].includes(domain)
      ) {
        form.setValue("website", `https://${domain}`);
      }
    }
  }, []);

  const onSubmit = () => {
    const values = form.getValues();
    setSetupTeamCreationInfosDatas({
      jobTitle: values.jobTitle || null,
      siret: values.siret || null,
      website: values.website || null,
      linkedin: values.linkedin || null,
      instagram: values.instagram || null,
      referralCode: null,
    });

    nextStep();
  };

  const ignoreStep = () => {
    setSetupTeamCreationInfosDatas({
      jobTitle: null,
      siret: null,
      website: null,
      linkedin: null,
      instagram: null,
      referralCode: null,
    });

    nextStep();
  };

  return (
    <div className="flex flex-col gap-7">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamCreation.informations.title")}
        </h1>
        <p className="text-sm">
          {t("setup.teamCreation.informations.description")}
        </p>
      </div>
      <div className="flex flex-col gap-7 items-center">
        <Form {...form}>
          <form className="space-y-6 w-full">
            <FieldJobTitle form={form} />
            <CompanySources form={form} />
          </form>
        </Form>
        <div className="space-x-2">
          <Button onClick={() => prevStep()} variant="outline">
            {t("setup.teamCreation.informations.buttons.previous")}
          </Button>
          <Button onClick={() => ignoreStep()} variant="outline">
            {t("setup.teamCreation.informations.buttons.later")}
            <FiChevronsRight className="ml-2 w-4 h-4" />
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)}>
            {t("setup.teamCreation.informations.buttons.continue")}
            <FiChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const FieldJobTitle = ({ form }: { form: UseFormReturn<any> }) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={form.control}
      name="jobTitle"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-lg font-semibold">
            {t("setup.teamCreation.informations.fields.jobTitle.label")}
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircleIcon className="inline-block ml-2 w-3 h-3" />
              </TooltipTrigger>
              <TooltipContent>
                {t("setup.teamCreation.informations.fields.jobTitle.tooltip")}
              </TooltipContent>
            </Tooltip>
          </FormLabel>
          <FormControl>
            <Input {...field} className="bg-background" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const CompanySources = ({ form }: { form: UseFormReturn<any> }) => {
  const { t } = useTranslation();
  const sources = [
    {
      name: "siret",
      title: t("setup.teamCreation.informations.fields.sources.siret.title"),
      placeholder: t(
        "setup.teamCreation.informations.fields.sources.siret.placeholder"
      ),
      helper: t("setup.teamCreation.informations.fields.sources.siret.tooltip"),
    },
    {
      name: "website",
      title: t("setup.teamCreation.informations.fields.sources.website.title"),
      placeholder: t(
        "setup.teamCreation.informations.fields.sources.website.placeholder"
      ),
      helper: t(
        "setup.teamCreation.informations.fields.sources.website.tooltip"
      ),
    },
    {
      name: "linkedin",
      title: t("setup.teamCreation.informations.fields.sources.linkedin.title"),
      placeholder: t(
        "setup.teamCreation.informations.fields.sources.linkedin.placeholder"
      ),
      helper: t(
        "setup.teamCreation.informations.fields.sources.linkedin.tooltip"
      ),
    },
    {
      name: "instagram",
      title: t(
        "setup.teamCreation.informations.fields.sources.instagram.title"
      ),
      placeholder: t(
        "setup.teamCreation.informations.fields.sources.instagram.placeholder"
      ),
      helper: t(
        "setup.teamCreation.informations.fields.sources.instagram.tooltip"
      ),
    },
  ];

  return (
    <div className="space-y-5 w-full">
      {sources.map((source) => (
        <FormField
          control={form.control}
          name={source.name as "siret" | "website" | "linkedin" | "instagram"}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-lg font-semibold">
                {source.title}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircleIcon className="inline-block ml-2 w-3 h-3" />
                  </TooltipTrigger>
                  <TooltipContent>{source.helper}</TooltipContent>
                </Tooltip>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  placeholder={source.placeholder}
                  className="bg-background"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </div>
  );
};
